import { useState, useEffect } from 'react';
import { FaTimes, FaCheckSquare, FaSquare, FaShieldAlt } from 'react-icons/fa';
import Select from 'react-select';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { updateDataWhenCreate, updateDataWhenUpdate } from '../../../../services/helpers';
import { api } from '../../../../api/client';
import { rolesApi } from '../../api/roles.api';
import { MODE } from '../../../../interaces/mode.interface';
import { STATUS } from '../../interfaces/roles.interface';
import { rolesSchema } from '../../schemas/roles.schema';
import { ENUM_ACTIONS } from '../../../../interaces/enums/enums.interface';
import styles from '../../styles/styles.module.css';
const PERM_LABELS = {
  CREATE: 'Crear',
  READ: 'Consultar',
  UPDATE: 'Editar',
  DELETE: 'Eliminar',
  EXPORT: 'Exportar',
  APPROVE: 'Aprobar',
};

function ModalRoles({ setEntities = () => {}, onClose, model = null, mode = MODE.CREATE }) {
  const isSuperadmin = mode === MODE.UPDATE && model?.name === 'SUPERADMIN';
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(rolesSchema),
    defaultValues: {
      name: '',
      description: '',
      status: true,
    },
  });

  useEffect(() => {
    if (mode == MODE.UPDATE && model) {
      console.log('vamos a editar');
      reset({
        name: model.name,
        description: model.description,
        status: model.status || true,
      });
    }
  }, []);
  const [matrix, setMatrix] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /*
   * Cargar matriz de privilegios
   * y establecer valores iniciales del formulario
   */
  useEffect(() => {
    setError('');
    setLoading(true);

    api
      .get('/privileges/matrix')
      .then((data) => {
        setMatrix(data);

        if (mode === MODE.UPDATE && model?.privileges) {
          const privilegeIds = new Set(model.privileges.map((privilege) => privilege.id));
          data.modules.forEach((module) => {
            const createPermission = module.permissions.find(
              (permission) => permission.permissionCode === 'CREATE'
            );
            const readPermission = module.permissions.find(
              (permission) => permission.permissionCode === 'READ'
            );

            if (
              createPermission &&
              readPermission &&
              privilegeIds.has(createPermission.privilegeId)
            ) {
              privilegeIds.add(readPermission.privilegeId);
            }
          });
          setSelected(privilegeIds);
        } else {
          setSelected(new Set());
        }
      })
      .catch((err) => {
        setError(err.message || 'No se pudo cargar la matriz de permisos.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [mode, model, reset]);
  useEffect(() => {
    if (model && mode === MODE.UPDATE) {
      reset({
        name: model?.name,
        description: model?.description,
        status: model?.status,
      });
    }
  }, [mode, model, reset]);

  /*
   * Seleccionar / deseleccionar un privilegio
   */
  const toggle = (permission, modulePermissions) => {
    setSelected((prev) => {
      const next = new Set(prev);

      if (next.has(permission.privilegeId)) {
        next.delete(permission.privilegeId);
        if (permission.permissionCode === 'CREATE' || permission.permissionCode === 'READ') {
          const dependentPermission = modulePermissions.find(
            (item) =>
              item.permissionCode === (permission.permissionCode === 'CREATE' ? 'READ' : 'CREATE')
          );
          if (dependentPermission) next.delete(dependentPermission.privilegeId);
        }
      } else {
        next.add(permission.privilegeId);
        if (permission.permissionCode === 'CREATE') {
          const readPermission = modulePermissions.find((item) => item.permissionCode === 'READ');
          if (readPermission) next.add(readPermission.privilegeId);
        }
      }

      return next;
    });
  };

  /*
   * Seleccionar / deseleccionar todos
   * los privilegios de un módulo
   */
  const toggleRow = (module) => {
    const ids = module.permissions.map((permission) => permission.privilegeId);

    const allChecked = ids.every((id) => selected.has(id));

    setSelected((prev) => {
      const next = new Set(prev);

      ids.forEach((id) => {
        if (allChecked) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });

      return next;
    });
  };

  /*
   * Seleccionar / deseleccionar una columna
   */
  const toggleCol = (permCode) => {
    if (!matrix) return;

    const modules = matrix.modules;
    const ids = modules
      .flatMap((module) => module.permissions)
      .filter((permission) => permission.permissionCode === permCode)
      .map((permission) => permission.privilegeId);

    const allChecked = ids.length > 0 && ids.every((id) => selected.has(id));

    setSelected((prev) => {
      const next = new Set(prev);

      modules.forEach((module) => {
        const permission = module.permissions.find((item) => item.permissionCode === permCode);
        if (!permission) return;

        if (allChecked) {
          next.delete(permission.privilegeId);
          if (permCode === 'CREATE' || permCode === 'READ') {
            const dependentPermission = module.permissions.find(
              (item) => item.permissionCode === (permCode === 'CREATE' ? 'READ' : 'CREATE')
            );
            if (dependentPermission) next.delete(dependentPermission.privilegeId);
          }
        } else {
          next.add(permission.privilegeId);
          if (permCode === 'CREATE') {
            const readPermission = module.permissions.find(
              (item) => item.permissionCode === 'READ'
            );
            if (readPermission) next.add(readPermission.privilegeId);
          }
        }
      });

      return next;
    });
  };

  /*
   * Seleccionar todos los privilegios
   */
  const handleSelectAll = () => {
    if (!matrix) return;

    const allIds = matrix.modules.flatMap((module) =>
      module.permissions.map((permission) => permission.privilegeId)
    );

    setSelected(new Set(allIds));
  };

  /*
   * Limpiar todos los privilegios
   */
  const handleDeselectAll = () => {
    setSelected(new Set());
  };

  /*
   * Guardar
   */
  const onSubmit = async (data) => {
    setError('');
    setSaving(true);

    try {
      const payload = {
        name: data.name.trim().toUpperCase().replace(/\s+/g, '_'),

        description: data.description.trim(),

        status: data.status,

        privilegeIds: [...selected],
      };

      if (mode === MODE.UPDATE) {
        const response = await rolesApi.update(model.id, payload);
        updateDataWhenUpdate(setEntities, response);
      } else {
        const response = await rolesApi.create(payload);
        updateDataWhenCreate(setEntities, response);

        toast.success('Rol creado exitosamente.');
      }

      onClose();
    } catch (err) {
      setError(err.message || err.error || 'Error al guardar el rol.');
    } finally {
      setSaving(false);
    }
  };

  const columns = matrix?.allPermissions || [];

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-sizes">
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center gap-2">
            <FaShieldAlt className={`${styles.iconRole}`} />

            <h2 className="modal-title">
              {mode === MODE.UPDATE
                ? ENUM_ACTIONS.Update + `Rol: ${model?.name || ''}`
                : ENUM_ACTIONS.Create + 'Rol'}
            </h2>
          </div>

          <button type="button" onClick={onClose} className="modal-close-btn">
            <FaTimes />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="modal-form">
          {/* Body */}
          <div className="modal-body">
            {error && <div className="error-text">{error}</div>}

            <p className="modal-description">
              Administra los datos generales del rol y los privilegios que tendrá asignados.
            </p>

            {/* Información general */}
            <div className="modal-div-columns">
              {/* Nombre */}
              <div className="modal-div-column">
                <label className="label-base">
                  Nombre del Rol <p className="modal-input-required">*</p>
                </label>

                <input
                  type="text"
                  {...register('name', {
                    onChange: (e) => {
                      e.target.value = e.target.value.toUpperCase();
                    },
                  })}
                  disabled={isSuperadmin}
                  placeholder="Ej: SUPERVISOR"
                  className="input-base"
                />

                {errors.name && <span className="error-text">{errors.name.message}</span>}

                {isSuperadmin && (
                  <span className="modal-description">
                    El nombre del rol SUPERADMIN no se puede modificar.
                  </span>
                )}
              </div>

              {/* Estatus */}
              <div className="modal-div-column">
                <label className="label-base">
                  Estatus<p className="modal-input-required">*</p>
                </label>

                <Controller
                  name="status"

                  control={control}
                  render={({ field }) => (
                    <Select
                      isDisabled={isSuperadmin}
                      options={STATUS}
                      getOptionLabel={(option) => option.value}
                      getOptionValue={(option) => option.key}
                      value={STATUS.find((option) => option.key === field.value) || null}
                      onChange={(option) => field.onChange(option?.key || false)}
                      placeholder="Selecciona un estatus"
                      isSearchable={false}
                    />
                  )}
                />
                {errors.status && <span className="error-text">{errors.status.message}</span>}
              </div>

              {/* Descripción */}
              <div className="modal-div-column-one">
                <label className="label-base">Descripción</label>
                <textarea
                  {...register('description')}
                  rows="2"
                  placeholder="Describe las responsabilidades y alcance del rol..."
                  className="input-base resize-none"
                />
                {errors.description && (
                  <span className="error-text">{errors.description.message}</span>
                )}
              </div>
            </div>

            {/* Matriz de permisos */}
            <div className={styles.permissionPackage}>
              <div className={styles.packageHeader}>
                <div>
                  <h3 className={styles.packageTitle}>Paquete de Permisos</h3>

                  <p className={styles.packageDescription}>
                    Haz clic en un módulo o columna para selección masiva
                  </p>
                </div>

                <div className={styles.packageActions}>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className={styles.selectAllButton}
                  >
                    <FaCheckSquare className={styles.selectAllIcon} />
                    Seleccionar todo
                  </button>

                  <span className={styles.actionSeparator}>|</span>

                  <button
                    type="button"
                    onClick={handleDeselectAll}
                    className={styles.clearSelectionButton}
                  >
                    <FaSquare className={styles.clearSelectionIcon} />
                    Limpiar
                  </button>

                  <span className={styles.selectedBadge}>{selected.size} privilegios</span>
                </div>
              </div>

              {/* Tabla */}
              <div className={styles.permissionTableContainer}>
                {loading ? (
                  <div className={styles.loadingState}>
                    <div className={styles.loadingSpinner}></div>
                    Cargando matriz de privilegios...
                  </div>
                ) : (
                  <table className={styles.permissionTable}>
                    <thead className={styles.permissionTableHead}>
                      <tr>
                        <th className={styles.moduleHeaderCell}>Módulo</th>

                        {columns.map((column) => (
                          <th
                            key={column}
                            onClick={() => toggleCol(column)}
                            title="Clic para alternar todos los permisos de esta columna"
                            className={styles.permissionHeaderCell}
                          >
                            {PERM_LABELS[column] || column}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className={styles.permissionTableBody}>
                      {matrix?.modules.map((module, index) => {
                        const permByCode = Object.fromEntries(
                          module.permissions.map((permission) => [
                            permission.permissionCode,
                            permission,
                          ])
                        );

                        const rowIds = module.permissions.map(
                          (permission) => permission.privilegeId
                        );

                        const allChecked =
                          rowIds.length > 0 && rowIds.every((id) => selected.has(id));

                        const someChecked = rowIds.some((id) => selected.has(id));

                        return (
                          <tr
                            key={module.code}
                            className={
                              index % 2 === 0 ? styles.permissionRowEven : styles.permissionRowOdd
                            }
                          >
                            {/* Módulo */}
                            <td className={styles.moduleCell}>
                              <label className={styles.moduleLabel}>
                                <input
                                  type="checkbox"
                                  checked={allChecked}
                                  ref={(element) => {
                                    if (element) {
                                      element.indeterminate = someChecked && !allChecked;
                                    }
                                  }}
                                  onChange={() => toggleRow(module)}
                                  className={styles.moduleCheckbox}
                                />

                                <span className={styles.moduleName}>{module.name}</span>
                              </label>
                            </td>

                            {/* Permisos */}
                            {columns.map((column) => {
                              const cell = permByCode[column];

                              return (
                                <td key={column} className={styles.permissionCell}>
                                  {cell ? (
                                    <input
                                      type="checkbox"
                                      checked={selected.has(cell.privilegeId)}
                                      onChange={() => toggle(cell, module.permissions)}
                                      title={`${cell.privilegeName} (${module.name})`}
                                      className={styles.permissionCheckbox}
                                    />
                                  ) : (
                                    <span className={styles.unavailablePermission}>—</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" onClick={onClose} disabled={saving} className="btn-cancel">
              Cancelar
            </button>

            <button type="submit" disabled={saving || loading} className="btn-primary">
              {saving ? 'Guardando...' : mode === MODE.UPDATE ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ModalRoles;
